import React from "react";

import useTranslation from "src/translations/useTranslation";
import { TravelAnalysisMessages } from "@shared/languages/interfaces";
import { TravelCycleOutcomes } from "@shared/constants/travel-cycle-causes.enum";
import { TravelCycleSummary } from "@shared/interfaces/travel-cycle-analysis.interface";

import { CYCLE_COMPLETE_COLOR, CYCLE_GROUP_OF, CYCLE_GROUPS, CYCLE_OUTCOMES } from "../../constants";
import { formatCount, formatPercent, percentOf } from "../../cycleUtils";
import { ChartContainer, ImpactBar, ImpactCards } from "../../styles";

/** Below this share of the bar, a segment hides its label. */
const MIN_LABELED_SEGMENT = 4;

/** Props for the CycleImpact organism. */
interface CycleImpactProps {
  summary: TravelCycleSummary | null;
  /** Click on a cause: open its drill-down. */
  onOutcomeClick: (outcome: TravelCycleOutcomes) => void;
}

/**
 * "Onde estão as falhas": one bar with the complete cycle and the four groups of failures, then one card
 * per group with its causes, largest first. Each cause opens the drill-down.
 */
const CycleImpact: React.FC<CycleImpactProps> = ({ summary, onOutcomeClick }) => {

  const { t } = useTranslation();
  const total = summary?.total ?? 0;
  const outcomes = summary?.outcomes ?? {};
  const countOf = (outcome: TravelCycleOutcomes) => outcomes[outcome] || 0;
  const groupTotal = (key: string) => CYCLE_OUTCOMES
    .filter((entry) => CYCLE_GROUP_OF[entry.outcome].key === key)
    .reduce((sum, entry) => sum + countOf(entry.outcome), 0);
  const segments = [{ key: "complete", name: t(TravelAnalysisMessages.cycleKpiComplete), color: CYCLE_COMPLETE_COLOR, count: summary?.complete ?? 0 }]
    .concat(CYCLE_GROUPS.map((group) => ({ key: group.key, name: t(group.nameKey), color: group.color, count: groupTotal(group.key) })));
  const largestCause = Math.max(1, ...CYCLE_OUTCOMES.map((entry) => countOf(entry.outcome)));

  return (
    <ChartContainer>
      <div className="chart-title">{t(TravelAnalysisMessages.cycleImpactTitle)}</div>
      <p className="chart-hint">{t(TravelAnalysisMessages.cycleImpactHint)}</p>
      {!total ? (
        <div className="chart-empty">{t(TravelAnalysisMessages.noGroupedData)}</div>
      ) : (
        <>
          <ImpactBar role="img" aria-label={segments.map((s) => `${s.name} ${formatPercent(percentOf(s.count, total))}`).join(", ")}>
            <div className="impact-stack">
              {segments.map((s) => {
                const share = percentOf(s.count, total);

                return (
                  <span key={s.key} style={{ width: `${share}%`, background: s.color }} title={`${s.name}: ${formatPercent(share)}`}>
                    {share >= MIN_LABELED_SEGMENT ? formatPercent(share) : ""}
                  </span>
                );
              })}
            </div>
            <div className="impact-legend">
              {segments.map((s) => (
                <span key={s.key}>
                  <i style={{ background: s.color }} />
                  {s.name}
                  <b>{formatPercent(percentOf(s.count, total))}</b>
                </span>
              ))}
            </div>
          </ImpactBar>
          <ImpactCards>
            {CYCLE_GROUPS.slice().sort((a, b) => groupTotal(b.key) - groupTotal(a.key)).map((group) => {
              const causes = CYCLE_OUTCOMES
                .filter((entry) => CYCLE_GROUP_OF[entry.outcome].key === group.key && countOf(entry.outcome))
                .sort((a, b) => countOf(b.outcome) - countOf(a.outcome));

              return (
                <article key={group.key} className="impact-card" style={{ borderTopColor: group.color }}>
                  <header>
                    <span className="impact-card-name">{t(group.nameKey)}</span>
                    <span className="impact-card-value">{formatPercent(percentOf(groupTotal(group.key), total))}</span>
                  </header>
                  <p className="impact-card-desc">
                    {`${t(group.descriptionKey)} ${formatCount(groupTotal(group.key))} ${t(TravelAnalysisMessages.tripsUnit)}.`}
                  </p>
                  {causes.length ? (
                    <ul>
                      {causes.map((entry) => {
                        const count = countOf(entry.outcome);

                        return (
                          <li key={entry.outcome}>
                            <button type="button" title={t(entry.descriptionKey)} onClick={() => onOutcomeClick(entry.outcome)}>
                              <span className="cause-name">{t(entry.nameKey)}</span>
                              <b>{formatPercent(percentOf(count, total))}</b>
                              <span className="cause-bar">
                                <i style={{ width: `${percentOf(count, largestCause)}%`, background: group.color }} />
                              </span>
                              <span className="cause-action">
                                {`${formatCount(count)} ${t(TravelAnalysisMessages.tripsUnit)} · ${t(entry.actionKey)}`}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <span className="impact-card-desc">{t(TravelAnalysisMessages.cycleNoFailures)}</span>
                  )}
                </article>
              );
            })}
          </ImpactCards>
        </>
      )}
    </ChartContainer>
  );
};

export default CycleImpact;
